import { api } from './client'
import type { ApiEnvelope, Counter, PaginatedResponse } from '@/types/api'

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface CounterQueryParams {
    deskNumber?: number
    name?: string
    unitId?: string
    diagramId?: string
    status?: number
    fromDate?: string
    toDate?: string
}

export interface DeskNumberResponse {
    id: string
    deskNumber: string
    deskName: string
}

export interface CounterWriteInput {
    deskNumber: number
    name: string
    unitId?: string
    diagramId: string
    status?: number
}

const COUNTER_BASE = '/v1/reception-desk-management'

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T> | T[] | null | undefined,
): PaginatedResponse<T> {
    if (!raw) return { items: [], total: 0, page: 1, limit: 10, totalPages: 0 }
    if (Array.isArray(raw)) return { items: raw, total: raw.length, page: 1, limit: Math.max(10, raw.length), totalPages: raw.length ? 1 : 0 }
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements || 0,
        page: (raw.number || 0) + 1,
        limit: raw.size || 10,
        totalPages: raw.totalPages || 0,
    }
}

function adaptCounterPage(
    raw: SpringPage<Counter> | PaginatedResponse<Counter> | Counter[] | null | undefined,
): PaginatedResponse<Counter> {
    return adaptPage(raw)
}

function toQueryParams(params: CounterQueryParams = {}) {
    return {
        deskNumber: params.deskNumber,
        name: params.name?.trim() || undefined,
        unitId: params.unitId || undefined,
        diagramId: params.diagramId || undefined,
        status: params.status,
        fromDate: params.fromDate,
        toDate: params.toDate,
    }
}

function getErrorMessage(error: any, fallback: string) {
    return error?.response?.data?.responseMessage || error?.message || fallback
}

export async function fetchCounters(
    page = 0,
    pageSize = 10,
    params: CounterQueryParams = {},
    signal?: AbortSignal,
): Promise<PaginatedResponse<Counter>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<Counter> | Counter[]>>(
            COUNTER_BASE,
            {
                params: { page, size: pageSize, ...toQueryParams(params) },
                signal,
            },
        )
        return adaptCounterPage(response.data.responseData)
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy danh sách quầy.'))
    }
}

export async function fetchDeskNumbers(
    page = 0,
    pageSize = 10,
    params: CounterQueryParams = {},
    signal?: AbortSignal,
): Promise<PaginatedResponse<DeskNumberResponse>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<DeskNumberResponse> | DeskNumberResponse[]>>(
            `${COUNTER_BASE}/desk-number`,
            {
                params: {
                    page,
                    size: pageSize,
                    unitId: params.unitId || undefined,
                    diagramId: params.diagramId || undefined,
                    status: params.status,
                },
                signal,
            },
        )
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy danh sách số quầy.'))
    }
}

export async function getCounterById(id: string, signal?: AbortSignal): Promise<Counter> {
    try {
        const response = await api.get<ApiEnvelope<Counter>>(`${COUNTER_BASE}/${id}`, { signal })
        return response.data.responseData
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy chi tiết quầy.'))
    }
}

export async function createCounter(data: CounterWriteInput): Promise<ApiEnvelope<Counter>> {
    try {
        const payload = {
            unitId: data.unitId,
            diagramId: data.diagramId,
            deskNumber: data.deskNumber,
            name: data.name.trim(),
        }
        const response = await api.post<ApiEnvelope<Counter>>(COUNTER_BASE, payload)
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi tạo quầy.'))
    }
}

export async function updateCounter(id: string, data: CounterWriteInput): Promise<ApiEnvelope<Counter>> {
    try {
        const payload = {
            unitId: data.unitId,
            diagramId: data.diagramId,
            deskNumber: data.deskNumber,
            name: data.name.trim(),
            status: data.status,
        }
        const response = await api.put<ApiEnvelope<Counter>>(`${COUNTER_BASE}/${id}`, payload)
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi cập nhật quầy.'))
    }
}

export async function updateCounterStatus(counter: Counter, newStatus: number): Promise<ApiEnvelope<Counter>> {
    const fullCounter = await getCounterById(counter.id)
    return updateCounter(counter.id, {
        ...fullCounter,
        status: newStatus,
    })
}

export async function deleteCounter(id: string): Promise<ApiEnvelope<unknown>> {
    try {
        const response = await api.delete<ApiEnvelope<unknown>>(`${COUNTER_BASE}/${id}`)
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi xóa quầy.'))
    }
}